import {
    ROLE_ADMIN,
    ROLE_POWER_USER,
    ROLE_DEMO_USER,
    ROLE_TICKET_ADMIN,
    ROLE_TICKET_USER,
    ROLE_MAN_ELECTRICITY_ADMIN,
    ROLE_MAN_ELECTRICITY_USER,
    ROLE_MAN_PLANNER,
    ROLE_MAN_SUPERVISOR,
    ROLE_MAN_SHOPFLOOR_MANAGER,
    ROLE_MAN_SHIFTBOARD_MANAGER,
    ROLE_MAN_TOOL_MANAGER,
    ROLE_MAN_PG_VIEWER,
    ROLE_MAN_DT_VIEWER,
} from "@/lib/constants";

import { USER_TAGS } from "@/lib/CommonTags.generated";
import type { IGetUsersResItem } from "@/lib/backend/users.generated.types";

export type UserWithRolesMap = IGetUsersResItem & {
    roles_map: UserRolesMap;
};

export type UserRolesMap = Map<string, boolean>;

export type UserRole = {
    id: string;
    label_id: string;
    deactivation_warning?: string;
    is_tag?: boolean;
};

/**
 * List of user roles with their corresponding role ID, label IDs, and optional properties.
 */
export const user_roles: UserRole[] = [
    {
        id: ROLE_ADMIN,
        label_id: "role.admin",
        deactivation_warning: "You cannot deactivate your admin role.",
    },
    {
        id: ROLE_POWER_USER,
        label_id: "role.power_user",
        deactivation_warning: "You cannot deactivate your power-user role.",
    },
    {
        id: ROLE_DEMO_USER,
        label_id: "role.demo_user",
        deactivation_warning: "You cannot deactivate your demo-user role.",
    },
    {
        id: ROLE_TICKET_ADMIN,
        label_id: "role.ticket_admin",
        deactivation_warning: "You cannot deactivate your ticket admin role.",
    },
    {
        id: ROLE_TICKET_USER,
        label_id: "role.ticket_user",
    },
    {
        id: ROLE_MAN_ELECTRICITY_ADMIN,
        label_id: "role.electricity_admin",
    },
    {
        id: ROLE_MAN_ELECTRICITY_USER,
        label_id: "role.electricity_user",
    },
    {
        id: ROLE_MAN_PLANNER,
        label_id: "role.planner",
    },
    {
        id: ROLE_MAN_SUPERVISOR,
        label_id: "role.supervisor",
    },
    {
        id: ROLE_MAN_SHOPFLOOR_MANAGER,
        label_id: "role.shopfloor_manager",
    },
    {
        id: ROLE_MAN_SHIFTBOARD_MANAGER,
        label_id: "role.shiftboard_manager",
    },
    {
        id: ROLE_MAN_TOOL_MANAGER,
        label_id: "role.tool_manager",
    },
    {
        id: ROLE_MAN_PG_VIEWER,
        label_id: "role.pg_viewer",
    },
    {
        id: ROLE_MAN_DT_VIEWER,
        label_id: "role.dt_viewer",
    },
    {
        id: USER_TAGS.kiosk,
        label_id: "role.kiosk_user",
        is_tag: true,
    },
];

/**
 * Initializes a map of user role's IDs as keys and boolean values set to false.
 */
export const initRolesMap = (): UserRolesMap =>
    user_roles.reduce((roles_map, role) => roles_map.set(role.id, false), new Map<string, boolean>());

/**
 * Adds a `roles_map` property to the given user object, mapping each role's ID to a boolean value
 * indicating whether the user has that role or tag.
 *
 * - The value is true if `user.roles` contains `role.id`.
 * - The specal case is for `rule.is_tag` - the value is true if the user has the tag set to true.
 */
export const addUserRolesMap = (user: IGetUsersResItem): UserWithRolesMap => ({
    ...user,
    roles_map: user_roles.reduce((user_roles, role) => {
        const value = role.is_tag ? user.tags[role.id] === "true" : user.roles.includes(role.id);
        return user_roles.set(role.id, value);
    }, new Map<string, boolean>()),
});

type GetVisibleRolesParams = {
    is_admin?: boolean;
    org_code?: string;
};

/**
 * Returns the list of visible user roles based on input flags.
 */
export const getVisibleRoles = ({ is_admin = false, org_code = "" }: GetVisibleRolesParams = {}): UserRole[] => {
    let roles = user_roles;

    if (!is_admin) {
        roles = roles.filter(({ id }) => id !== ROLE_ADMIN);
    }

    if (org_code === "domel") {
        roles = roles.filter(({ id }) => id !== ROLE_MAN_SHIFTBOARD_MANAGER);
    }

    return roles;
};

/**
 * Retrieves the deactivation warning message for a given user role ID if there is any.
 */
export const getSelfDeactivationWarning = (role_id: string): string | void => {
    const role = user_roles.find(({ id }) => id === role_id);
    return role && role.deactivation_warning;
};
